import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
    AccountSummary,
    EquityPosition,
    EquityOrderResult,
} from '../../api';
import {
    Alert,
    Badge,
    Box,
    Button,
    Container,
    Header,
    SpaceBetween,
    Table,
} from '@cloudscape-design/components';
import { trading212AccountApi } from '../../client/clients';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { formatMoney, formatRelativeTime } from '../../util/formatMoney';
import { classifyError, alertTypeForKind } from '../../util/errorClassifier';

// ---------------------------------------------------------------------------
// Retry helper — silent backoff on rate-limit; only surface error after giving up
// ---------------------------------------------------------------------------

const sleep = (ms: number) => new Promise<void>(res => setTimeout(res, ms));

async function withRetry<T>(fn: () => Promise<T>, attempts = 3, backoffMs = 2000): Promise<T> {
    for (let i = 0; i < attempts; i++) {
        try {
            return await fn();
        } catch (err: unknown) {
            if (classifyError(err).kind !== 'rate-limit' || i === attempts - 1) throw err;
            await sleep(backoffMs * (i + 1));
        }
    }
    throw new Error('unreachable');
}

// ---------------------------------------------------------------------------
// Ina-error → user-facing message. Backend returns 400 with a text/JSON body
// when the Ina credentials aren't configured yet; surface that as a friendly
// notice rather than a generic "something went wrong".
// ---------------------------------------------------------------------------

function inaNotConfiguredMessage(error: unknown): string {
    const e = error as {
        response?: { status?: number; data?: unknown };
        message?: string;
    } | null | undefined;
    const status = e?.response?.status;
    const rawData = e?.response?.data;
    const dataStr =
        typeof rawData === 'string'
            ? rawData
            : (rawData && typeof rawData === 'object' && 'message' in rawData && typeof (rawData as { message?: unknown }).message === 'string')
                ? ((rawData as { message: string }).message)
                : '';
    const lower = (dataStr || e?.message || '').toLowerCase();
    if (lower.includes('not configured') || lower.includes('ina')) {
        return 'Ina account credentials not configured. Add them on the Credentials page.';
    }
    if (status === 400) {
        return 'Ina account credentials not configured. Add them on the Credentials page.';
    }
    const classified = classifyError(error);
    return classified.userMessage || 'Ina account is unavailable.';
}

// ---------------------------------------------------------------------------
// Position merge — aggregate same-ticker holdings across accounts
// ---------------------------------------------------------------------------

type MergedPosition = EquityPosition;

function mergePositions(primary: EquityPosition[], ina: EquityPosition[]): MergedPosition[] {
    const byTicker = new Map<string, MergedPosition>();
    const upsert = (p: EquityPosition) => {
        if (!p.ticker) return;
        const existing = byTicker.get(p.ticker);
        if (!existing) {
            byTicker.set(p.ticker, { ...p });
            return;
        }
        const qA = existing.quantity ?? 0;
        const qB = p.quantity ?? 0;
        const totalQty = qA + qB;
        const costA = (existing.averagePricePaid ?? 0) * qA;
        const costB = (p.averagePricePaid ?? 0) * qB;
        const totalCost = costA + costB;
        existing.quantity = totalQty;
        existing.averagePricePaid = totalQty > 0 ? totalCost / totalQty : existing.averagePricePaid;
        existing.quantityAvailableForTrading = (existing.quantityAvailableForTrading ?? 0) + (p.quantityAvailableForTrading ?? 0);
        existing.quantityInPies = (existing.quantityInPies ?? 0) + (p.quantityInPies ?? 0);
        existing.ppl = (existing.ppl ?? 0) + (p.ppl ?? 0);
    };
    primary.forEach(upsert);
    ina.forEach(upsert);
    return Array.from(byTicker.values());
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Strip Trading212 ticker suffixes: "AAPL_US_EQ" → "AAPL" */
function cleanTicker(raw: string | null | undefined): string {
    if (!raw) return '—';
    const clean = raw.split('_')[0];
    return clean || '—';
}

/** Derive the instrument's native currency from a Trading212 ticker suffix. */
function instrumentCurrency(ticker: string | null | undefined): string {
    if (!ticker) return 'USD';
    if (ticker.includes('_US_')) return 'USD';
    if (ticker.includes('_GB_') || ticker.includes('_GBEX_')) return 'GBP';
    return 'USD';
}

function fmt(value: number | null | undefined, currency?: string): string {
    if (value === undefined || value === null) return '—';
    if (currency) return formatMoney(value, currency);
    // Quantity formatting: show up to 6 decimals but strip trailing zeros
    return Number.isInteger(value)
        ? value.toLocaleString('en-GB')
        : value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 6 });
}

/** Format an ISO timestamp as relative time with the absolute date as a title tooltip. */
function RelativeTimestamp({ iso }: { iso: string | null | undefined }): React.ReactElement {
    if (!iso) return <span>—</span>;
    let date: Date;
    try {
        date = new Date(iso);
        if (Number.isNaN(date.getTime())) return <span>{iso}</span>;
    } catch {
        return <span>{iso}</span>;
    }
    const absolute = date.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
    const relative = formatRelativeTime(date);
    return (
        <span title={absolute} style={{ cursor: 'default', borderBottom: '1px dotted currentColor' }}>
            {relative}
        </span>
    );
}

/** Colour-coded badge for order status. */
function StatusBadge({ status }: { status: string | null | undefined }): React.ReactElement {
    if (!status) return <span>—</span>;
    const colorMap: Record<string, 'green' | 'blue' | 'red' | 'grey'> = {
        FILLED: 'green',
        PARTIALLY_FILLED: 'blue',
        CANCELLED: 'grey',
        REJECTED: 'red',
        NEW: 'blue',
        PENDING: 'blue',
    };
    const colour = colorMap[status.toUpperCase()] ?? 'grey';
    return <Badge color={colour}>{status}</Badge>;
}

/** Badge for order type. */
function TypeBadge({ type }: { type: string | null | undefined }): React.ReactElement {
    if (!type) return <span>—</span>;
    const colorMap: Record<string, 'green' | 'blue' | 'red' | 'grey'> = {
        BUY: 'green',
        SELL: 'red',
        LIMIT_BUY: 'blue',
        LIMIT_SELL: 'grey',
        STOP_SELL: 'red',
    };
    const colour = colorMap[type.toUpperCase()] ?? 'grey';
    return <Badge color={colour}>{type}</Badge>;
}

// ---------------------------------------------------------------------------
// Stat tile
// ---------------------------------------------------------------------------

interface StatTileProps {
    label: string;
    value: string;
    subValue?: string;       // secondary line, e.g. percentage change
    positive?: boolean | null; // null = neutral (no colour)
}

const StatTile: React.FC<StatTileProps> = ({ label, value, subValue, positive }) => {
    const accentColour =
        positive === null || positive === undefined
            ? 'var(--color-border-divider-default, #d1d5db)'
            : positive
            ? 'var(--color-charts-green-500, #1d8102)'
            : 'var(--color-charts-red-500, #d91515)';

    const valueColour =
        positive === null || positive === undefined
            ? 'inherit'
            : positive
            ? 'var(--color-charts-green-500, #1d8102)'
            : 'var(--color-charts-red-500, #d91515)';

    const trendArrow =
        positive === null || positive === undefined ? '' : positive ? ' ↑' : ' ↓';

    return (
        <div
            style={{
                flex: '1 1 200px',
                minWidth: 0,
                padding: '16px 20px',
                background: 'var(--color-background-container-content, #fff)',
                border: '1px solid var(--color-border-container-top, #d1d5db)',
                borderTop: `3px solid ${accentColour}`,
                borderRadius: '8px',
            }}
        >
            <Box variant="awsui-key-label" color="text-body-secondary">
                {label}
            </Box>
            <Box variant="h2" fontWeight="bold" fontSize="display-l" color="inherit">
                <span style={{ color: valueColour }}>{value}{trendArrow}</span>
            </Box>
            {subValue && (
                <Box variant="small" color="text-body-secondary">
                    {subValue}
                </Box>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// P&L cell helper
// ---------------------------------------------------------------------------

function PnlCell({
    pnl,
    costBasis,
    currency,
    pct,
}: {
    pnl: number;
    costBasis: number;
    currency: string;
    pct?: number;
}): React.ReactElement {
    const colour =
        pnl > 0
            ? 'var(--color-charts-green-500, #1d8102)'
            : pnl < 0
            ? 'var(--color-charts-red-500, #d91515)'
            : 'inherit';

    const sign = pnl >= 0 ? '+' : '';
    const absStr = `${sign}${formatMoney(pnl, currency)}`;
    const pctStr =
        pct !== undefined
            ? ` (${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%)`
            : costBasis !== 0
            ? ` (${sign}${((pnl / costBasis) * 100).toFixed(2)}%)`
            : '';

    return (
        <span style={{ color: colour, whiteSpace: 'nowrap' }}>
            {absStr}
            {pctStr && <span style={{ fontSize: '0.85em', opacity: 0.85 }}>{pctStr}</span>}
        </span>
    );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const PortfolioPage: React.FC = () => {
    useDocumentTitle('Portfolio');

    // Summary state — one per account so the top bar can sum across them
    const [primarySummary, setPrimarySummary] = useState<AccountSummary | null>(null);
    const [inaSummary, setInaSummary] = useState<AccountSummary | null>(null);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [summaryError, setSummaryError] = useState<string>('');
    const [inaError, setInaError] = useState<string>('');

    // Positions state (merged across both accounts)
    const [positions, setPositions] = useState<EquityPosition[]>([]);
    const [positionsLoading, setPositionsLoading] = useState(false);
    const [positionsError, setPositionsError] = useState<string>('');

    // Orders state (combined across both accounts)
    const [orders, setOrders] = useState<EquityOrderResult[]>([]);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [ordersError, setOrdersError] = useState<string>('');

    // -----------------------------------------------------------------------
    // Data fetching — both accounts in parallel; Ina errors degrade gracefully
    // -----------------------------------------------------------------------

    const fetchSummary = useCallback(async () => {
        setSummaryLoading(true);
        setSummaryError('');
        setInaError('');
        try {
            const [primaryRes, inaRes] = await Promise.all([
                withRetry(() => trading212AccountApi.getAccountSummary1('primary')),
                withRetry(() => trading212AccountApi.getAccountSummary1('ina'))
                    .catch((e: unknown) => ({ __error: e } as const)),
            ]);
            setPrimarySummary(primaryRes.data);
            if ('__error' in inaRes) {
                setInaError(inaNotConfiguredMessage(inaRes.__error));
                setInaSummary(null);
            } else {
                setInaSummary(inaRes.data);
            }
        } catch (err: unknown) {
            const classified = classifyError(err, { onRetry: fetchSummary });
            setSummaryError(classified.userMessage);
            setPrimarySummary(null);
        } finally {
            setSummaryLoading(false);
        }
    }, []);

    const fetchPositions = useCallback(async () => {
        setPositionsLoading(true);
        setPositionsError('');
        try {
            const [primaryRes, inaRes] = await Promise.all([
                withRetry(() => trading212AccountApi.getPositions1('primary')),
                withRetry(() => trading212AccountApi.getPositions1('ina'))
                    .catch(() => ({ data: [] as EquityPosition[] })),
            ]);
            const merged = mergePositions(primaryRes.data ?? [], inaRes.data ?? []);
            const sorted = merged.sort((a, b) => {
                // Prefer GBP market value for sorting; fall back to native price × qty
                const mv = (p: EquityPosition) =>
                    p.currentPriceInAccountCurrency != null
                        ? (p.currentPriceInAccountCurrency ?? 0) * (p.quantity ?? 0)
                        : (p.currentPrice ?? 0) * (p.quantity ?? 0);
                return mv(b) - mv(a);
            });
            setPositions(sorted);
        } catch (err: unknown) {
            const classified = classifyError(err, { onRetry: fetchPositions });
            setPositionsError(classified.userMessage);
            setPositions([]);
        } finally {
            setPositionsLoading(false);
        }
    }, []);

    const fetchOrders = useCallback(async () => {
        setOrdersLoading(true);
        setOrdersError('');
        try {
            const [primaryRes, inaRes] = await Promise.all([
                withRetry(() => trading212AccountApi.getOrders1('primary')),
                withRetry(() => trading212AccountApi.getOrders1('ina'))
                    .catch(() => ({ data: [] as EquityOrderResult[] })),
            ]);
            const combined = [...(primaryRes.data ?? []), ...(inaRes.data ?? [])]
                .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
            setOrders(combined);
        } catch (err: unknown) {
            const classified = classifyError(err, { onRetry: fetchOrders });
            setOrdersError(classified.userMessage);
            setOrders([]);
        } finally {
            setOrdersLoading(false);
        }
    }, []);

    const refreshAll = useCallback(() => {
        fetchSummary();
        fetchPositions();
        fetchOrders();
    }, [fetchSummary, fetchPositions, fetchOrders]);

    const initialLoadDone = useRef(false);
    useEffect(() => {
        if (initialLoadDone.current) return;
        initialLoadDone.current = true;
        refreshAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // -----------------------------------------------------------------------
    // Derived values for summary tiles — sums across Primary + Ina
    // -----------------------------------------------------------------------

    const sumNumber = (a: number | null | undefined, b: number | null | undefined): number =>
        (a ?? 0) + (b ?? 0);

    const hasAnySummary = primarySummary !== null || inaSummary !== null;

    const totalValueSum    = sumNumber(primarySummary?.totalValue, inaSummary?.totalValue);
    const availableSum     = sumNumber(primarySummary?.cash?.availableToTrade, inaSummary?.cash?.availableToTrade);
    const investedSum      = sumNumber(primarySummary?.investments?.currentValue, inaSummary?.investments?.currentValue);
    const unrealizedPnlSum = sumNumber(primarySummary?.investments?.unrealizedProfitLoss, inaSummary?.investments?.unrealizedProfitLoss);
    const realizedPnlSum   = sumNumber(primarySummary?.investments?.realizedProfitLoss, inaSummary?.investments?.realizedProfitLoss);
    const totalCostSum     = sumNumber(primarySummary?.investments?.totalCost, inaSummary?.investments?.totalCost);
    const reservedSum      = sumNumber(primarySummary?.cash?.reservedForOrders, inaSummary?.cash?.reservedForOrders);

    const currency = primarySummary?.currency ?? inaSummary?.currency ?? 'GBP';
    const unrealizedPct = totalCostSum !== 0
        ? `${unrealizedPnlSum >= 0 ? '+' : ''}${((unrealizedPnlSum / totalCostSum) * 100).toFixed(2)}% of cost`
        : undefined;

    // -----------------------------------------------------------------------
    // Positions table column definitions
    // -----------------------------------------------------------------------

    const positionColumnDefinitions = [
        {
            id: 'ticker',
            header: 'Ticker',
            cell: (p: EquityPosition) => (
                <strong>{cleanTicker(p.ticker)}</strong>
            ),
            sortingField: 'ticker',
            width: 110,
            minWidth: 90,
        },
        {
            id: 'quantity',
            header: 'Qty',
            cell: (p: EquityPosition) => fmt(p.quantity),
            sortingField: 'quantity',
            width: 90,
            minWidth: 70,
        },
        {
            id: 'avgPricePaid',
            header: 'Avg Price',
            cell: (p: EquityPosition) =>
                fmt(p.averagePricePaid, instrumentCurrency(p.ticker)),
            sortingField: 'averagePricePaid',
            width: 130,
            minWidth: 100,
        },
        {
            id: 'currentPrice',
            header: 'Price',
            cell: (p: EquityPosition) =>
                fmt(p.currentPrice, instrumentCurrency(p.ticker)),
            sortingField: 'currentPrice',
            width: 120,
            minWidth: 100,
        },
        {
            id: 'marketValue',
            header: `Value (${currency})`,
            cell: (p: EquityPosition) => {
                const posCurrency = p.currentPriceInAccountCurrency != null
                    ? currency
                    : instrumentCurrency(p.ticker);
                const mv = p.currentPriceInAccountCurrency != null
                    ? (p.currentPriceInAccountCurrency ?? 0) * (p.quantity ?? 0)
                    : (p.currentPrice ?? 0) * (p.quantity ?? 0);
                return fmt(mv, posCurrency);
            },
            width: 130,
            minWidth: 100,
        },
        {
            id: 'pnl',
            header: `P&L (${currency})`,
            cell: (p: EquityPosition) => {
                if (p.ppl != null) {
                    // ppl is in account currency (GBP); use native-price ratio for accurate %
                    const nativePct = p.averagePricePaid != null && p.averagePricePaid !== 0
                        ? ((p.currentPrice ?? 0) - p.averagePricePaid) / p.averagePricePaid * 100
                        : undefined;
                    return <PnlCell pnl={p.ppl} costBasis={0} currency={currency} pct={nativePct} />;
                }
                const posCurrency = instrumentCurrency(p.ticker);
                const pnl =
                    ((p.currentPrice ?? 0) - (p.averagePricePaid ?? 0)) *
                    (p.quantity ?? 0);
                const costBasis = (p.averagePricePaid ?? 0) * (p.quantity ?? 0);
                return <PnlCell pnl={pnl} costBasis={costBasis} currency={posCurrency} />;
            },
            width: 180,
            minWidth: 140,
        },
        {
            id: 'availableToTrade',
            header: 'Available',
            cell: (p: EquityPosition) => fmt(p.quantityAvailableForTrading),
            width: 90,
            minWidth: 70,
        },
        {
            id: 'inPies',
            header: 'In Pies',
            cell: (p: EquityPosition) =>
                p.quantityInPies != null && p.quantityInPies > 0
                    ? fmt(p.quantityInPies)
                    : <span style={{ opacity: 0.4 }}>—</span>,
            width: 80,
            minWidth: 60,
        },
        {
            id: 'openedAt',
            header: 'Opened',
            cell: (p: EquityPosition) => <RelativeTimestamp iso={p.createdAt} />,
            sortingField: 'createdAt',
            width: 120,
            minWidth: 100,
        },
    ];

    // -----------------------------------------------------------------------
    // Orders table column definitions
    // -----------------------------------------------------------------------

    const orderColumnDefinitions = [
        {
            id: 'ticker',
            header: 'Ticker',
            cell: (o: EquityOrderResult) => (
                <strong>{cleanTicker(o.ticker)}</strong>
            ),
            sortingField: 'ticker',
            width: 110,
            minWidth: 90,
        },
        {
            id: 'type',
            header: 'Type',
            cell: (o: EquityOrderResult) => <TypeBadge type={o.type} />,
            sortingField: 'type',
            width: 120,
            minWidth: 90,
        },
        {
            id: 'status',
            header: 'Status',
            cell: (o: EquityOrderResult) => <StatusBadge status={o.status} />,
            sortingField: 'status',
            width: 140,
            minWidth: 110,
        },
        {
            id: 'quantity',
            header: 'Qty',
            cell: (o: EquityOrderResult) => fmt(o.quantity),
            width: 80,
            minWidth: 60,
        },
        {
            id: 'filledQuantity',
            header: 'Filled',
            cell: (o: EquityOrderResult) =>
                o.filledQuantity != null ? fmt(o.filledQuantity) : <span style={{ opacity: 0.4 }}>—</span>,
            width: 80,
            minWidth: 60,
        },
        {
            id: 'limitPrice',
            header: 'Limit',
            cell: (o: EquityOrderResult) =>
                o.limitPrice != null ? fmt(o.limitPrice, currency) : <span style={{ opacity: 0.4 }}>—</span>,
            width: 100,
            minWidth: 80,
        },
        {
            id: 'stopPrice',
            header: 'Stop',
            cell: (o: EquityOrderResult) =>
                o.stopPrice != null ? fmt(o.stopPrice, currency) : <span style={{ opacity: 0.4 }}>—</span>,
            width: 100,
            minWidth: 80,
        },
        {
            id: 'createdAt',
            header: 'Placed',
            cell: (o: EquityOrderResult) => <RelativeTimestamp iso={o.createdAt} />,
            sortingField: 'createdAt',
            width: 120,
            minWidth: 100,
        },
        {
            id: 'updatedAt',
            header: 'Updated',
            cell: (o: EquityOrderResult) => <RelativeTimestamp iso={o.updatedAt} />,
            sortingField: 'updatedAt',
            width: 120,
            minWidth: 100,
        },
        {
            id: 'id',
            header: 'Order ID',
            cell: (o: EquityOrderResult) => (
                <span style={{ fontFamily: 'monospace', fontSize: '0.85em', opacity: 0.7 }}>
                    {o.id ?? '—'}
                </span>
            ),
            width: 90,
            minWidth: 70,
        },
    ];

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------

    const isRefreshing = summaryLoading || positionsLoading || ordersLoading;

    const summaryClassified = summaryError
        ? classifyError(new Error(summaryError), { onRetry: fetchSummary })
        : null;
    const positionsClassified = positionsError
        ? classifyError(new Error(positionsError), { onRetry: fetchPositions })
        : null;
    const ordersClassified = ordersError
        ? classifyError(new Error(ordersError), { onRetry: fetchOrders })
        : null;

    return (
        <SpaceBetween size="l">
            {/* Page header with global refresh */}
            <Header
                variant="h1"
                actions={
                    <Button
                        iconName="refresh"
                        variant="normal"
                        loading={isRefreshing}
                        onClick={refreshAll}
                        ariaLabel="Refresh all portfolio data"
                    >
                        Refresh
                    </Button>
                }
            >
                Portfolio
            </Header>

            {inaError && (
                <Alert
                    type="warning"
                    header="Ina account"
                    dismissible
                    onDismiss={() => setInaError('')}
                >
                    {inaError}
                </Alert>
            )}

            {/* ---------------------------------------------------------------- */}
            {/* Account Summary                                                  */}
            {/* ---------------------------------------------------------------- */}
            <Container
                header={
                    <Header
                        variant="h2"
                        actions={
                            <Button
                                iconName="refresh"
                                variant="icon"
                                loading={summaryLoading}
                                onClick={fetchSummary}
                                ariaLabel="Refresh account summary"
                            />
                        }
                    >
                        Account Summary
                    </Header>
                }
            >
                {summaryClassified && (
                    <Box margin={{ bottom: 'm' }}>
                        <Alert
                            type={alertTypeForKind(summaryClassified.kind)}
                            header={summaryClassified.userMessage}
                            dismissible
                            onDismiss={() => setSummaryError('')}
                            action={
                                summaryClassified.suggestedAction ? (
                                    <Button
                                        onClick={summaryClassified.suggestedAction.onClick}
                                        href={summaryClassified.suggestedAction.href}
                                        loading={summaryLoading}
                                    >
                                        {summaryClassified.suggestedAction.label}
                                    </Button>
                                ) : undefined
                            }
                        />
                    </Box>
                )}

                {summaryLoading && !hasAnySummary ? (
                    <Box textAlign="center" color="text-body-secondary" padding="l">
                        Loading account summary…
                    </Box>
                ) : (
                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '12px',
                        }}
                    >
                        <StatTile
                            label={`Total Value (${currency})`}
                            value={fmt(totalValueSum, currency)}
                            positive={null}
                        />
                        <StatTile
                            label="Available to Trade"
                            value={fmt(availableSum, currency)}
                            positive={null}
                        />
                        <StatTile
                            label="Invested"
                            value={fmt(investedSum, currency)}
                            positive={null}
                        />
                        <StatTile
                            label="Unrealised P&L"
                            value={fmt(unrealizedPnlSum, currency)}
                            subValue={unrealizedPct}
                            positive={unrealizedPnlSum >= 0}
                        />
                        {realizedPnlSum !== 0 && (
                            <StatTile
                                label="Realised P&L"
                                value={fmt(realizedPnlSum, currency)}
                                positive={realizedPnlSum >= 0}
                            />
                        )}
                        {reservedSum > 0 && (
                            <StatTile
                                label="Reserved for Orders"
                                value={fmt(reservedSum, currency)}
                                positive={null}
                            />
                        )}
                    </div>
                )}
            </Container>

            {/* ---------------------------------------------------------------- */}
            {/* Positions                                                        */}
            {/* ---------------------------------------------------------------- */}
            <Container
                header={
                    <Header
                        variant="h2"
                        counter={positions.length > 0 ? `(${positions.length})` : undefined}
                        actions={
                            <Button
                                iconName="refresh"
                                variant="icon"
                                loading={positionsLoading}
                                onClick={fetchPositions}
                                ariaLabel="Refresh positions"
                            />
                        }
                    >
                        Open Positions
                    </Header>
                }
            >
                {positionsClassified && (
                    <Box margin={{ bottom: 'm' }}>
                        <Alert
                            type={alertTypeForKind(positionsClassified.kind)}
                            header={positionsClassified.userMessage}
                            dismissible
                            onDismiss={() => setPositionsError('')}
                            action={
                                positionsClassified.suggestedAction ? (
                                    <Button
                                        onClick={positionsClassified.suggestedAction.onClick}
                                        href={positionsClassified.suggestedAction.href}
                                        loading={positionsLoading}
                                    >
                                        {positionsClassified.suggestedAction.label}
                                    </Button>
                                ) : undefined
                            }
                        />
                    </Box>
                )}
                <Table<EquityPosition>
                    items={positions}
                    columnDefinitions={positionColumnDefinitions}
                    loading={positionsLoading}
                    loadingText="Loading positions…"
                    empty={
                        <Box textAlign="center" color="text-body-secondary" padding="xl">
                            <Box variant="h3" color="text-body-secondary">
                                No open positions
                            </Box>
                            <Box variant="p" color="text-body-secondary" margin={{ top: 'xs' }}>
                                Your open equity positions will appear here once you have active holdings.
                            </Box>
                        </Box>
                    }
                    variant="embedded"
                    stripedRows
                    stickyHeader
                />
            </Container>

            {/* ---------------------------------------------------------------- */}
            {/* Active Orders                                                    */}
            {/* ---------------------------------------------------------------- */}
            <Container
                header={
                    <Header
                        variant="h2"
                        counter={orders.length > 0 ? `(${orders.length})` : undefined}
                        actions={
                            <Button
                                iconName="refresh"
                                variant="icon"
                                loading={ordersLoading}
                                onClick={fetchOrders}
                                ariaLabel="Refresh orders"
                            />
                        }
                    >
                        Active Orders
                    </Header>
                }
            >
                {ordersClassified && (
                    <Box margin={{ bottom: 'm' }}>
                        <Alert
                            type={alertTypeForKind(ordersClassified.kind)}
                            header={ordersClassified.userMessage}
                            dismissible
                            onDismiss={() => setOrdersError('')}
                            action={
                                ordersClassified.suggestedAction ? (
                                    <Button
                                        onClick={ordersClassified.suggestedAction.onClick}
                                        href={ordersClassified.suggestedAction.href}
                                        loading={ordersLoading}
                                    >
                                        {ordersClassified.suggestedAction.label}
                                    </Button>
                                ) : undefined
                            }
                        />
                    </Box>
                )}
                <Table<EquityOrderResult>
                    items={orders}
                    columnDefinitions={orderColumnDefinitions}
                    loading={ordersLoading}
                    loadingText="Loading orders…"
                    empty={
                        <Box textAlign="center" color="text-body-secondary" padding="xl">
                            <Box variant="h3" color="text-body-secondary">
                                No active orders
                            </Box>
                            <Box variant="p" color="text-body-secondary" margin={{ top: 'xs' }}>
                                Pending and recently placed orders will appear here.
                            </Box>
                        </Box>
                    }
                    variant="embedded"
                    stripedRows
                    stickyHeader
                />
            </Container>
        </SpaceBetween>
    );
};
